import React, { useState, useEffect } from "react";
import CustomInput from "../components/common/CustomInput";
import CustomModal from "../components/common/CustomModal";
import CustomButton from "../components/common/CustomButton";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  createUser,
  getUsers,
  updateUser,
  deleteUser,
} from "../utils/authenticationUtils.js";
import {
  TruckIcon,
  WrenchIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

const styles = {
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    padding: "16px 24px",
    paddingTop: 16,
    minHeight: "100vh",
    maxWidth: 1400,
    margin: "0 auto",
  },
  titleContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 24,
    animation: "fadeIn 0.5s ease-in-out",
  },
  title: {
    fontSize: 32,
    color: COLORS.text,
    fontFamily: FONTS.bold,
    fontWeight: 700,
  },
  searchInput: {
    marginBottom: 16,
  },
  userItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: 12,
    marginBottom: 16,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    border: `1px solid ${COLORS.tertiary}`,
    transition: "all 0.3s ease",
    animation: "fadeIn 0.5s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: `0 6px 16px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  userIcon: {
    width: 28,
    height: 28,
    color: COLORS.primary,
  },
  userDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  userEmail: {
    fontSize: 20,
    color: COLORS.text,
    fontFamily: FONTS.bold,
    fontWeight: 600,
  },
  userRoles: {
    fontSize: 16,
    color: COLORS.gray,
    fontFamily: FONTS.regularInter,
  },
  actionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  actionButton: {
    padding: 10,
    borderRadius: 10,
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  deleteButton: {
    "&:hover": {
      backgroundColor: COLORS.error,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  addButton: {
    position: "fixed",
    bottom: 32,
    right: 32,
    border: "none",
    borderRadius: 32,
    width: 64,
    height: 64,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.primary,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
    cursor: "pointer",
    color: COLORS.white,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: COLORS.secondary,
      color: COLORS.primary,
      transform: "scale(1.1)",
      boxShadow: `0 6px 20px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.3)`,
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  },
  emptyText: {
    fontSize: 18,
    color: COLORS.gray,
    textAlign: "center",
    marginTop: 32,
    fontFamily: FONTS.regularInter,
  },
  switchContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    padding: 12,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    border: `1px solid ${COLORS.gray}`,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
  switchLabel: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  switchIcon: {
    width: 20,
    height: 20,
    color: COLORS.primary,
  },
  button: {
    marginTop: 12,
  },
  loader: {
    fontSize: 18,
    color: COLORS.text,
    textAlign: "center",
    marginVertical: 32,
    fontFamily: FONTS.regularInter,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  loadingSpinner: {
    border: `3px solid ${COLORS.gray}`,
    borderTop: `3px solid ${COLORS.primary}`,
    borderRadius: "50%",
    width: 24,
    height: 24,
    animation: "spin 1s linear infinite",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    userItem: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 12,
      padding: 16,
    },
    actionsContainer: {
      width: "100%",
      justifyContent: "flex-end",
    },
    addButton: {
      bottom: 16,
      right: 16,
      width: 56,
      height: 56,
    },
  },
};

const AdminUserManagementScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isModalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState({
    isDriver: false,
    isEquipmentMan: false,
  });
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchUsers = async () => {
    try {
      const userList = await getUsers();
      // Sort users alphabetically by email
      const sortedUsers = userList.sort((a, b) =>
        (a.email || "").localeCompare(b.email || "")
      );
      setUsers(sortedUsers);
    } catch (error) {
      alert("Failed to fetch users");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSaveUser = async () => {
    if (!email || (!editingUser && !password)) {
      alert("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      if (editingUser) {
        await updateUser(editingUser.id, email, roles);
        alert("User updated successfully");
      } else {
        await createUser(email, password, roles);
        alert("User added successfully");
      }
      setEmail("");
      setPassword("");
      setRoles({ isDriver: false, isEquipmentMan: false });
      setEditingUser(null);
      setModalVisible(false);
      await fetchUsers();
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEmail(user.email);
    setRoles(user.roles);
    setPassword("");
    setModalVisible(true);
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }
    setDeletingId(id);
    try {
      await deleteUser(id);
      alert("User deleted successfully");
      await fetchUsers();
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  // Filter users based on search query
  const filteredUsers = (users || []).filter((user) =>
    (user.email || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderUserItem = (item) => (
    <div style={styles.userItem} key={item.id}>
      <div style={styles.userInfo}>
        <UserIcon style={styles.userIcon} />
        <div style={styles.userDetails}>
          <span style={styles.userEmail}>{item.email}</span>
          <span style={styles.userRoles}>
            {item.roles.isDriver && item.roles.isEquipmentMan
              ? "Driver & Equipment Man"
              : item.roles.isDriver
              ? "Driver"
              : item.roles.isEquipmentMan
              ? "Equipment Man"
              : "No roles assigned"}
            {(item.roles.isDriver || item.roles.isEquipmentMan) && " ("}
            {item.roles.isDriver && (
              <TruckIcon
                style={{
                  ...styles.switchIcon,
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              />
            )}
            {item.roles.isDriver && item.roles.isEquipmentMan && ", "}
            {item.roles.isEquipmentMan && (
              <WrenchIcon
                style={{
                  ...styles.switchIcon,
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              />
            )}
            {(item.roles.isDriver || item.roles.isEquipmentMan) && ")"}
          </span>
        </div>
      </div>
      <div style={styles.actionsContainer}>
        <button
          style={{ ...styles.actionButton }}
          onClick={() => handleEditUser(item)}
        >
          <PencilIcon width={24} height={24} stroke={COLORS.primary} />
        </button>
        <button
          style={{ ...styles.actionButton, ...styles.deleteButton }}
          onClick={() => handleDeleteUser(item.id)}
          disabled={deletingId === item.id}
        >
          {deletingId === item.id ? (
            <div style={styles.loadingSpinner}></div>
          ) : (
            <TrashIcon width={24} height={24} stroke={COLORS.error} />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.safeArea}>
      <div style={styles.container}>
        <div style={styles.titleContainer}>
          <h1 style={styles.title}>User Management</h1>
        </div>
        <CustomInput
          value={searchQuery}
          onChangeText={setSearchQuery}
          icon="search"
          placeholder="Search by email"
          style={styles.searchInput}
        />
        {fetching ? (
          <div style={styles.loader}>
            <div style={styles.loadingSpinner}></div>
            <span>Loading...</span>
          </div>
        ) : (
          <div>
            {filteredUsers.length === 0 ? (
              <span style={styles.emptyText}>No users available</span>
            ) : (
              filteredUsers.map(renderUserItem)
            )}
          </div>
        )}
        <button
          style={styles.addButton}
          onClick={() => {
            setEditingUser(null);
            setEmail("");
            setPassword("");
            setRoles({ isDriver: false, isEquipmentMan: false });
            setModalVisible(true);
          }}
          disabled={loading}
        >
          <PlusIcon width={30} height={30} stroke={COLORS.white} />
        </button>
        <CustomModal
          isVisible={isModalVisible}
          onClose={() => setModalVisible(false)}
          title={editingUser ? "Edit User" : "Add New User"}
          style={{
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.gray}`,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            borderRadius: 12,
            padding: 16,
            maxWidth: 500,
            width: "90%",
            margin: "0 auto",
          }}
        >
          <CustomInput
            label="Email"
            value={email}
            onChangeText={setEmail}
            icon="mail"
            placeholder="Enter email"
            type="email"
            error={email && !email.includes("@") ? "Invalid email address" : ""}
            style={{ marginBottom: 12 }}
          />
          {!editingUser && (
            <CustomInput
              label="Password"
              value={password}
              onChangeText={setPassword}
              secureTextEntry={true}
              icon="lock"
              placeholder="Enter password"
              style={{ marginBottom: 12 }}
            />
          )}
          <div style={styles.switchContainer}>
            <span style={styles.switchLabel}>
              <TruckIcon style={styles.switchIcon} />
              Driver
            </span>
            <input
              type="checkbox"
              checked={roles.isDriver}
              onChange={(e) =>
                setRoles({ ...roles, isDriver: e.target.checked })
              }
              style={{ cursor: "pointer" }}
            />
          </div>
          <div style={styles.switchContainer}>
            <span style={styles.switchLabel}>
              <WrenchIcon style={styles.switchIcon} />
              Equipment Man
            </span>
            <input
              type="checkbox"
              checked={roles.isEquipmentMan}
              onChange={(e) =>
                setRoles({ ...roles, isEquipmentMan: e.target.checked })
              }
              style={{ cursor: "pointer" }}
            />
          </div>
          <CustomButton
            title={
              loading
                ? "Saving..."
                : editingUser
                ? "Update User"
                : "Create User"
            }
            onPress={handleSaveUser}
            style={styles.button}
            disabled={loading}
          />
        </CustomModal>
      </div>
    </div>
  );
};

export default AdminUserManagementScreen;
